import { create } from 'zustand';

/**
 * UI-only state for tasks
 * This store handles UI state that doesn't need to be persisted to the server
 * Server state is managed by React Query
 *
 * Use this store for:
 * - Task editing UI state
 * - Drag and drop interaction states
 * - Mobile view states
 * - Task creation UI state
 * - Filtering UI state
 */
interface TaskUIState {
  // Task editing state
  isCreating: boolean;
  showDescription: boolean;
  newTaskTitle: string;
  newTaskDescription: string;
  newTaskTags: string[];
  newTaskDeadline: string | null;
  newTaskAssignedTo: string | null;

  // Task deletion confirmation state
  itemToDelete: string | null;

  // Active status/column for task operations
  activeColumn: string | null;
  activeTopicId?: string; // Track active topic ID

  // Drag and drop state
  dragState: { columnId: string | null; index: number | null };
  dragOverColumn: string | null;
  targetDropColumnId: string | null;

  // Recording state for voice input
  recordingColumn: string | null;

  // Mobile-specific state
  mobileActiveColumn: string;

  // Context menu state for task cards
  activeTaskMenuId: string | null;

  // Column management UI state
  columnToDelete: string | null;
  isEditingColumnName: string | null;
  isEditingColumnColor: string | null;

  // Note: Tracking functions removed - using invalidation-based approach

  // Task UI state actions
  setIsCreating: (isCreating: boolean) => void;
  setShowDescription: (show: boolean) => void;
  setNewTaskTitle: (title: string) => void;
  setNewTaskDescription: (description: string) => void;
  setNewTaskTags: (tags: string[]) => void;
  setNewTaskDeadline: (deadline: string | null) => void;
  setNewTaskAssignedTo: (userId: string | null) => void;
  setItemToDelete: (taskId: string | null) => void;
  setActiveColumn: (columnId: string | null) => void;
  setDragState: (state: { columnId: string | null; index: number | null }) => void;
  setDragOverColumn: (columnId: string | null) => void;
  setRecordingColumn: (columnId: string | null) => void;
  setMobileActiveColumn: (columnId: string) => void;
  setTargetDropColumnId: (columnId: string | null) => void;

  // Column management actions
  setColumnToDelete: (columnId: string | null) => void;
  setIsEditingColumnName: (columnId: string | null) => void;
  setIsEditingColumnColor: (columnId: string | null) => void;

  // Context menu actions
  openTaskMenu: (taskId: string) => void;
  closeTaskMenu: () => void;

  // Reset only the task form inputs
  resetTaskForm: () => void;

  // Set active topic ID
  setActiveTopicId: (topicId: string) => void;

  // Reset all state
  resetTaskUIState: () => void;
}

/**
 * Store for managing task UI state
 * This only handles UI-specific state that doesn't need to be persisted to the server
 *
 * @example
 * // Using the store in a component
 * const {
 *   isCreating,
 *   activeColumn,
 *   setIsCreating,
 *   setActiveColumn
 * } = useTaskUIStore();
 *
 * // Start creating a task in a specific column
 * const handleAddTask = (columnId) => {
 *   setActiveColumn(columnId);
 *   setIsCreating(true);
 * };
 *
 * // Reset UI state when unmounting or changing views
 * useEffect(() => {
 *   return () => {
 *     useTaskUIStore.getState().resetTaskUIState();
 *   };
 * }, []);
 */
export const useTaskUIStore = create<TaskUIState>(set => ({
  // Task editing state
  isCreating: false,
  showDescription: false,
  newTaskTitle: '',
  newTaskDescription: '',
  newTaskTags: [],
  newTaskDeadline: null,
  newTaskAssignedTo: null,

  // Task deletion confirmation state
  itemToDelete: null,

  // Active status/column for task operations
  activeColumn: null,
  activeTopicId: undefined,

  // Drag and drop state
  dragState: { columnId: null, index: null },
  dragOverColumn: null,
  targetDropColumnId: null,

  // Recording state for voice input
  recordingColumn: null,

  // Mobile-specific state
  mobileActiveColumn: 'todo',

  // Context menu state for task cards
  activeTaskMenuId: null,

  // Column management UI state
  columnToDelete: null,
  isEditingColumnName: null,
  isEditingColumnColor: null,

  // Task UI state actions
  setIsCreating: isCreating => set({ isCreating }),

  setShowDescription: show => set({ showDescription: show }),

  setNewTaskTitle: title => set({ newTaskTitle: title }),

  setNewTaskDescription: description => set({ newTaskDescription: description }),

  setNewTaskTags: tags => set({ newTaskTags: tags }),

  setNewTaskDeadline: deadline => set({ newTaskDeadline: deadline }),

  setNewTaskAssignedTo: userId => set({ newTaskAssignedTo: userId }),

  setItemToDelete: taskId => set({ itemToDelete: taskId }),

  setActiveColumn: columnId => set({ activeColumn: columnId }),

  setDragState: state => set({ dragState: state }),

  setDragOverColumn: columnId => set({ dragOverColumn: columnId }),

  setRecordingColumn: columnId => set({ recordingColumn: columnId }),

  setMobileActiveColumn: columnId => set({ mobileActiveColumn: columnId }),

  setTargetDropColumnId: columnId => set({ targetDropColumnId: columnId }),

  // Column management actions
  setColumnToDelete: columnId => set({ columnToDelete: columnId }),

  setIsEditingColumnName: columnId => set({ isEditingColumnName: columnId }),

  setIsEditingColumnColor: columnId => set({ isEditingColumnColor: columnId }),

  // Context menu actions
  openTaskMenu: taskId => set({ activeTaskMenuId: taskId }),

  closeTaskMenu: () => set({ activeTaskMenuId: null }),

  // Reset only task form inputs (for use after task creation)
  resetTaskForm: () =>
    set({
      newTaskTitle: '',
      newTaskDescription: '',
      newTaskTags: [],
      newTaskDeadline: null,
      newTaskAssignedTo: null,
      showDescription: false,
    }),

  // Set active topic ID
  setActiveTopicId: topicId => set({ activeTopicId: topicId }),

  // Reset all state
  resetTaskUIState: () =>
    set(state => {
      return {
        isCreating: false,
        showDescription: false,
        newTaskTitle: '',
        newTaskDescription: '',
        newTaskTags: [],
        newTaskDeadline: null,
        newTaskAssignedTo: null,
        itemToDelete: null,
        activeColumn: null,
        dragState: { columnId: null, index: null },
        dragOverColumn: null,
        targetDropColumnId: null,
        recordingColumn: null,
        // Do not reset mobileActiveColumn as it's persistent
        mobileActiveColumn: state.mobileActiveColumn,
        activeTaskMenuId: null,
        columnToDelete: null,
        isEditingColumnName: null,
        isEditingColumnColor: null,
      };
    }),
}));
